import dayjs from "dayjs";
import {
  CONTENT_TYPE_JSON,
  TOKEN_EXPIRED_MESSAGE,
} from "../../utils/apiConstants.js";
import { logger, sanitizeError } from "../../utils/logger.js";
import {
  getLoggedAuthToken,
  getProfileFieldValue,
  refreshToken,
  signOut,
} from "../../utils/common.js";

/**
 * HTTP helpers for event attendee registration.
 *
 * A signed-in user whose type is not "4" is prefilled from `attendee/user/:id` and the form stays locked.
 * Type "4" is the guest path: the form starts empty and can be edited.
 *
 * Select values are stringified so they match option ids. Null is left null so a missing field still fails the completeness check.
 * Expectations and commercial interests are required only when the event flags say so.
 *
 * `ambitoOperaciones` is taken from the root record when the selected profile index is 0,
 * and from the matching `perfiles` row otherwise. The POST body does not send the profile picker itself.
 *
 * An expired token refreshes the session and retries. A failed refresh signs the user out.
 *
 * Sector loading does not send the bearer token. Registration loading and the attendee POST do.
 * A missing token on the POST is logged and the dialog stays open.
 * Validation failures for expectations or commercial interests are returned to the hook,
 * which is what turns on the field error state.
 * The attendee POST sends the signed-in user id and the current time as `fechaInscripcion`.
 * A successful POST closes the dialog. A non-expiry error is logged and the dialog stays open.
 */
/**
 * JSON headers, with a bearer token only when one is passed.
 * Sector loading calls this with no token. Registration calls pass the session token.
 * @param {string} [token] - Access token.
 * @returns {Object} Fetch headers.
 */
const jsonHeaders = (token) => ({
  "Content-Type": CONTENT_TYPE_JSON,
  ...(token ? { Authorization: `Bearer ${token}` } : {}),
});

/**
 * Refreshes the access token. Signs the user out and sends them home when refresh fails.
 * @param {function} navigate - Router navigate.
 * @returns {Promise<boolean>} True when a new token was stored.
 */
const recoverSession = async (navigate) => {
  const newTokens = await refreshToken();
  if (!newTokens) {
    await signOut();
    navigate("/");
    return false;
  }
  return true;
};

/**
 * Stringifies select values so they match option ids. Null stays null.
 * @param {unknown} value - API field.
 * @returns {string|null|undefined} String form, or the original nullish value.
 */
const stringOrSame = (value) => (value != null ? String(value) : value);

/**
 * Normalizes an attendee record for the form selects.
 * @param {Object} data - Response from `attendee/user/:id`.
 * @returns {Object} Same record with sector, branch, phone, scope, and worker count stringified.
 */
export const registrationFromApi = (data) => ({
  ...data,
  ambitoOperaciones: stringOrSame(data.ambitoOperaciones),
  sector: stringOrSame(data.sector),
  subSector: stringOrSame(data.subSector),
  rama: stringOrSame(data.rama),
  numeroTotalTrabajadores: stringOrSame(data.numeroTotalTrabajadores),
  telefono: stringOrSame(data.telefono),
});

/**
 * Whether any required form value is still null.
 * Expectations and commercial interests are ignored when the event does not require them.
 * @param {Object} formData - Registration form.
 * @param {Object} event - Event flags `expectativasRequerido` and `interesesComercialesRequerido`.
 * @returns {boolean} True when a required value is null.
 */
export const isRegistrationIncomplete = (formData, event) => {
  const tempFormData = { ...formData };
  if (!event.expectativasRequerido) {
    delete tempFormData.expectativas;
  }
  if (!event.interesesComercialesRequerido) {
    delete tempFormData.interesesComerciales;
  }
  return Object.values(tempFormData).some((value) => value === null);
};

/**
 * Whether a required free-text field was left empty.
 * @param {boolean} required - Event flag for this field.
 * @param {unknown} value - Current field value.
 * @returns {boolean} True when the event requires the field and the value is null, undefined, or "".
 */
const requiredTextMissing = (required, value) =>
  Boolean(required) && (value === null || value === undefined || value === "");

/**
 * Loads sectors. Does not send a bearer token. Retries once after a token-expired payload.
 * @param {function} navigate - Router navigate, used when refresh fails.
 * @returns {Promise<Array|null>} Sector list, or null on failure or sign-out.
 */
export const loadSectors = async (navigate) => {
  try {
    const response = await fetch(`${process.env.REACT_APP_ROOT_API}sector`, {
      method: "GET",
      headers: jsonHeaders(),
    });
    const data = await response.json();
    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const recovered = await recoverSession(navigate);
      if (!recovered) {
        return null;
      }
      return loadSectors(navigate);
    }
    return data;
  } catch (error) {
    logger.error("Error", { err: sanitizeError(error) });
    return null;
  }
};

/**
 * Loads the signed-in user's attendee record and normalizes it for the form.
 * @param {function} navigate - Router navigate, used when refresh fails.
 * @returns {Promise<Object|null>} Normalized registration, or null on failure or sign-out.
 */
export const loadRegistrationInfo = async (navigate) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}attendee/user/${localStorage.getItem("userId")}`,
      {
        method: "GET",
        headers: jsonHeaders(localStorage.getItem("token")),
      },
    );
    const data = await response.json();
    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const recovered = await recoverSession(navigate);
      if (!recovered) {
        return null;
      }
      return loadRegistrationInfo(navigate);
    }
    return registrationFromApi(data);
  } catch (error) {
    logger.error("Error", { err: sanitizeError(error) });
    return null;
  }
};

/**
 * Builds the attendee POST body.
 * Scope comes from the root record for profile 0 and from the matching `perfiles` row otherwise.
 * The profile picker fields are removed. `fechaInscripcion` is the current UTC time.
 * @param {Object} formData - Registration form, including `perfilSeleccionado`.
 * @param {Object} event - Event being joined. Uses `event._id`.
 * @returns {Object} Body for `POST attendee`.
 */
const buildAttendanceData = (formData, event) => {
  const selectedProfileIndex = formData.perfilSeleccionado ?? 0;
  const ambitoOperaciones = getProfileFieldValue(
    formData,
    "ambitoOperaciones",
    selectedProfileIndex,
    formData.ambitoOperaciones,
  );
  const restFormData = { ...formData };
  delete restFormData.perfilSeleccionado;
  delete restFormData.perfiles;
  return {
    ...restFormData,
    ambitoOperaciones,
    idEvento: event._id,
    idUsuario: localStorage.getItem("userId"),
    fechaInscripcion: dayjs().utc("z").toISOString(),
  };
};

/**
 * Posts one attendee registration.
 * @param {Object} attendanceData - Body from `buildAttendanceData`.
 * @param {string} authToken - Bearer token.
 * @returns {Promise<Object>} `ok`, or `expired` with status and message.
 */
const postAttendee = async (attendanceData, authToken) => {
  const response = await fetch(`${process.env.REACT_APP_ROOT_API}attendee`, {
    method: "POST",
    headers: jsonHeaders(authToken),
    body: JSON.stringify(attendanceData),
  });
  if (response.ok) {
    await response.json();
    return { ok: true };
  }
  const errorData = await response.json();
  return {
    ok: false,
    expired: errorData?.message === TOKEN_EXPIRED_MESSAGE,
    status: response.status,
    message: errorData.message,
  };
};

/**
 * Validates required text, posts the registration, and retries after a token refresh.
 * @param {Object} params
 * @param {Object} params.formData - Registration form.
 * @param {Object} params.event - Event flags and id.
 * @param {function} params.navigate - Router navigate, used when refresh fails.
 * @param {function(): void} params.onClose - Closes the dialog after a successful POST.
 * @returns {Promise<Object>} `ok`, `invalid`, `missingToken`, `signedOut`, or `failed`.
 */
export const registerAttendee = async ({ formData, event, navigate, onClose }) => {
  if (requiredTextMissing(event.expectativasRequerido, formData.expectativas)) {
    return { invalid: true };
  }
  if (
    requiredTextMissing(
      event.interesesComercialesRequerido,
      formData.interesesComerciales,
    )
  ) {
    return { invalid: true };
  }

  const authToken = getLoggedAuthToken();
  if (!authToken) {
    logger.error("Failed to get the token");
    return { missingToken: true };
  }

  try {
    const result = await postAttendee(
      buildAttendanceData(formData, event),
      authToken,
    );
    if (result.ok) {
      onClose();
      return { ok: true };
    }
    if (result.expired) {
      const recovered = await recoverSession(navigate);
      if (!recovered) {
        return { signedOut: true };
      }
      return registerAttendee({ formData, event, navigate, onClose });
    }
    logger.error("Failed to register in event:", {
      data: [result.status, result.message],
    });
    return { failed: true };
  } catch (error) {
    logger.error("Error:", { err: sanitizeError(error) });
    return { failed: true };
  }
};
